import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import type { Warehouse } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { countUnmapped } from "@/modules/warehouses/channelMapping";
import { projectWarehouse } from "@/modules/warehouses/projections";
import { ChannelMapping } from "@/modules/warehouses/screens/ChannelMapping";
import type { SectionScreenProps } from "@/modules/types";

/**
 * The warehouse master, plus a read-only detail panel.
 *
 * The panel exists because `fulfilsOrders` is not decoration: a location
 * that only receives returns still holds stock but must never be offered as
 * a dispatch source, and that distinction does not fit next to a code and a
 * city in a table cell. Selecting a row is the only interaction here —
 * there is no write path (M6-12): a warehouse is mirrored from the
 * channel's locations, and the mapping is shown, not edited.
 */
export function WarehouseList({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["warehouses", "list", filter, search, sort, limit, cursor],
    queryFn: () =>
      data.warehouses.list({ filter, search, sort, limit, cursor }),
  });

  if (query.isPending) return <TableSkeleton />;

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  // No selection yet on first load reads as "nothing to show" rather than
  // an empty panel — with only two warehouses in the fixture data, the
  // first row is the reasonable thing to already be looking at.
  const activeId = selectedId ?? query.data.items[0]?.id ?? null;
  const selected =
    query.data.items.find((warehouse) => warehouse.id === activeId) ?? null;
  const unmapped = countUnmapped(query.data.items);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {unmapped > 0 ? (
          <div className="text-muted-foreground bg-muted/30 flex items-start gap-2 border-b px-4 py-3 text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p>
              {unmapped} of {query.data.items.length} warehouses on this page
              have no channel location mapped and will not display stock figures
              (FR-WHS-4).
            </p>
          </div>
        ) : null}

        <DataTable
          fields={fields}
          rows={query.data.items}
          getRowId={(warehouse) => warehouse.id}
          project={projectWarehouse}
          sort={sort}
          onToggleSort={onToggleSort}
          onRowClick={(warehouse) => {
            setSelectedId(warehouse.id);
          }}
          emptyState={<NoMatches onClear={onClearFilter} />}
          footer={
            <Pagination
              page={query.data}
              limit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          }
        />
      </div>

      <WarehouseDetail warehouse={selected} />
    </div>
  );
}

function WarehouseDetail({ warehouse }: { warehouse: Warehouse | null }) {
  if (!warehouse) {
    return (
      <Card className="w-full shrink-0 lg:w-80">
        <CardContent className="text-muted-foreground text-sm">
          No warehouse to show.
        </CardContent>
      </Card>
    );
  }

  const address = warehouse.address;
  const cityLine = address
    ? [address.city, address.province, address.zip].filter(Boolean).join(", ")
    : "";

  return (
    <Card className="w-full shrink-0 lg:w-80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {warehouse.name}
          <Badge variant="outline" className="font-normal">
            {warehouse.code}
          </Badge>
        </CardTitle>
        <CardDescription>
          {warehouse.isActive ? "Active" : "Inactive"}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4 text-sm">
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Address
          </p>
          {address ? (
            <address className="leading-relaxed not-italic">
              {address.line1 ? <div>{address.line1}</div> : null}
              {address.line2 ? <div>{address.line2}</div> : null}
              {cityLine ? <div>{cityLine}</div> : null}
              {address.country ? <div>{address.country}</div> : null}
            </address>
          ) : (
            <p className="text-muted-foreground">No address on file.</p>
          )}
        </div>

        <div className="space-y-1">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Dispatch
          </p>
          {/*
            The flag the ticket exists for: a location that only receives
            returns still holds stock, but a picker must never be offered it
            as a source to ship from. That has to read as a warning, not a
            second "Active"-style badge someone skims past.
          */}
          {warehouse.fulfilsOrders ? (
            <Badge className="font-normal">Can dispatch orders</Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-warning/60 bg-warning/10 text-warning font-normal"
            >
              Receiving only — not a dispatch source
            </Badge>
          )}
        </div>

        <ChannelMapping warehouse={warehouse} />
      </CardContent>
    </Card>
  );
}
